import { useConfig } from 'config/use-config';
import useUserCountry from 'hooks/common/use-user-country';
import { EffectiveCart } from 'interfaces/cart';
import { DiscountType } from 'interfaces/order';
import { useCallback, useMemo } from 'react';
import { countFrozenItems } from 'utils/cart';

interface Input {
  effectiveCart?: EffectiveCart;
  /**
   * When true, uses the default DE MOV instead of Austria-specific MOV.
   * This allows users who entered checkout with a valid cart to complete their order
   * even if we later detect they're Austrian.
   */
  isInCheckout?: boolean;
}

interface Output {
  /** The amount of money that is missing to reach the MOV */
  insufficientMoney: number;
  /* MOV, decimal is omitted from the value e.g 2015 meaning 25€ 15cent" */
  minimumBasketValue: number;
  /** True if cart amount is not sufficient for MOV */
  shouldShowMinimumBasketNotice: boolean;
}

/**
 * Hooks to check minimum order value conditions.
 * @TODO : Logics are differed from getFrozenProductsCount
 */
const useMOVConditions = ({ effectiveCart, isInCheckout = false }: Input): Output => {
  const {
    minimumBasketValue: minimumBasketValueConst,
    minimumBasketValueAT,
    minimumBasketValueBarfAT,
    minimumBasketValueFixedCoupon,
  } = useConfig();
  const { isAustria } = useUserCountry();

  const hasBarf = useMemo(() => {
    if (!effectiveCart?.items) return false;
    return countFrozenItems(effectiveCart.items, false) > 0;
  }, [effectiveCart?.items]);

  const minimumBasketValue: number = useMemo(() => {
    // This is necessary in order to allow micro-influencers to use a fix coupon and perform a checkout below the standard MOV.
    const hasFixedCoupon = Boolean(
      effectiveCart?.discount?.fixedValue && effectiveCart?.discount?.type === DiscountType.COUPON
    );

    if (hasFixedCoupon) return minimumBasketValueFixedCoupon;

    // Skip Austria-specific MOV during checkout to allow users who entered checkout
    // with a valid cart to complete their order even if we later detect they're Austrian.
    if (isInCheckout) return minimumBasketValueConst;

    // Austria-specific MOV logic (only applied in cart, not checkout)
    if (isAustria) {
      if (hasBarf && minimumBasketValueBarfAT !== undefined) return minimumBasketValueBarfAT;
      if (minimumBasketValueAT) return minimumBasketValueAT;
    }

    return minimumBasketValueConst;
  }, [
    effectiveCart?.discount?.fixedValue,
    effectiveCart?.discount?.type,
    isInCheckout,
    hasBarf,
    isAustria,
    minimumBasketValueAT,
    minimumBasketValueBarfAT,
    minimumBasketValueConst,
    minimumBasketValueFixedCoupon,
  ]);

  const calculateTotalCredits = useCallback(() => {
    return effectiveCart?.discount?.type === DiscountType.CREDIT
      ? effectiveCart?.discount?.totalDiscount
      : 0;
  }, [effectiveCart]);

  const shouldShowMinimumBasketNotice: boolean = useMemo(() => {
    const totalCredits = calculateTotalCredits();
    const cartTotal = effectiveCart?.itemsTotal ?? 0;
    return cartTotal + totalCredits < minimumBasketValue;
  }, [effectiveCart, minimumBasketValue, calculateTotalCredits]);

  const insufficientMoney = useMemo(() => {
    const totalCredits = calculateTotalCredits();
    const cartTotal = effectiveCart?.itemsTotal ?? 0;
    return (minimumBasketValue - cartTotal - totalCredits) / 100;
  }, [effectiveCart, minimumBasketValue, calculateTotalCredits]);

  return {
    insufficientMoney,
    minimumBasketValue,
    shouldShowMinimumBasketNotice,
  };
};

export default useMOVConditions;
